import type { QuoteDocument, PresentationEstimator } from 'paintscout'
import React, { useMemo } from 'react'
import type { QuoteContextValue } from './QuoteContext'
import { QuoteContext } from './QuoteContext'

export interface QuoteContextProviderProps {
  quote: QuoteDocument
  children?: React.ReactNode
  tableView?: 'area' | 'substrate'
  presentationPage?: string
  sidebarPage?: string
  isCustomerView?: boolean
  isPresentation?: boolean

  onAccept?: (pending?: boolean) => any
  onDecline?: () => any
  refetchQuote?: () => Promise<QuoteDocument | null>

  estimator?: PresentationEstimator

  handleOptionalItems?: {
    onSelectOptionalItems: (keys: string[]) => void
    selectedOptionalItems: string[]
  }

  // for EditableQuoteContextProvider to use
  contextValue?: QuoteContextValue
}

function QuoteContextProvider(props: QuoteContextProviderProps) {
  const {
    children,
    quote,
    presentationPage,
    sidebarPage,
    estimator,
    onAccept,
    onDecline,
    refetchQuote,
    isCustomerView,
    isPresentation,
    handleOptionalItems,
    contextValue
  } = props

  let tableView: 'area' | 'substrate'
  if (props.tableView) {
    tableView = props.tableView
  } else if (quote && quote.defaultView) {
    tableView = quote.defaultView as 'area' | 'substrate'
  } else {
    tableView = 'area'
  }

  // Memoize the context value so it only gets a new identity when one of its inputs actually
  // changes. Previously this rebuilt on every render, re-rendering every useQuote() consumer
  // on any parent render (a source of the editor's interaction lag — #21450). In the editable
  // path `contextValue` is now stabilized upstream, so the value stays referentially stable
  // until real data changes.
  const value: QuoteContextValue = useMemo(
    () => ({
      quote,
      tableView,
      sidebarPage,
      estimator,
      onAccept,
      isCustomerView,
      isPresentation,
      isEditable: false,
      onDecline,
      handleOptionalItems,
      ...contextValue,
      // After the spread: both the app and customer view supply refetchQuote as a provider prop,
      // never through contextValue — keep it from being clobbered by a future contextValue field.
      refetchQuote,
      presentationPage
    }),
    [
      quote,
      tableView,
      sidebarPage,
      estimator,
      onAccept,
      refetchQuote,
      isCustomerView,
      isPresentation,
      onDecline,
      handleOptionalItems,
      contextValue,
      presentationPage
    ]
  )

  return <QuoteContext.Provider value={value}>{children}</QuoteContext.Provider>
}

export default QuoteContextProvider
